<template>
  <div class="upload-v2">
    <!-- 上传方式切换 -->
    <div class="upload-tabs">
      <div class="tab-item" :class="{ active: uploadType === 1 }" @click="uploadType = 1">
        <app-icon  class="app-icon" name="link"></app-icon>
        <span>链接上传</span>
      </div>
      <div class="tab-item" :class="{ active: uploadType === 2 }" @click="uploadType = 2">
        <app-icon  class="app-icon" name="cloud_upload"></app-icon>
        <span>视频上传</span>
      </div>
    </div>

    <!-- 链接上传 -->
    <div class="upload-panel" v-if="uploadType === 1">
      <textarea class="url-textarea" v-model="inputUrl" placeholder="一行一个链接，回车换行" rows="6"></textarea>
      <div class="panel-actions">
        <div class="tab-option clear-option" @click="clearAfterSubmit = !clearAfterSubmit"
          :class="{ on: clearAfterSubmit }">
          提交后清空: {{ clearAfterSubmit ? '开' : '关' }}
        </div>
        <button class="btn-sm btn-outline" @click="pasteClipboard">
          <app-icon  class="app-icon" name="content_paste"></app-icon>粘贴
        </button>
        <button class="btn-sm btn-primary" @click="confirmUrls">
          <app-icon  class="app-icon" name="check"></app-icon>确认链接
        </button>
      </div>
    </div>

    <!-- 视频上传 -->
    <div class="upload-panel upload-drop" v-if="uploadType === 2" @click="$refs.fileUpload.click()" @dragover.prevent
      @drop.prevent="handleFileDrop">
      <app-icon  class="app-icon drop-icon" name="cloud_upload"></app-icon>
      <p class="drop-text">将文件拖到此处，或点击上传</p>
      <p class="drop-hint">MP4 / MOV，单个 ≤ 600MB，最多 {{ limit }} 个</p>
      <input ref="fileUpload" type="file" accept=".mp4,.mov" multiple style="display:none" @change="handleFileSelect">
    </div>

    <!-- 已上传列表 -->
    <div class="upload-list" v-if="urlList.length > 0">
      <div class="list-header">
        <span class="list-title">已上传视频 ({{ urlList.length }})</span>
      </div>
      <div class="list-body">
        <div class="list-item" v-for="(item, index) in urlList" :key="index">
          <span class="item-index">{{ index + 1 }}</span>
          <span class="item-icon loading" v-if="item.zt === 'uploading'">
            <i class="el-icon-loading"></i>
          </span>
          <span class="item-icon success" v-else-if="item.zt === 'success'">
            <app-icon  class="app-icon" name="check_circle"></app-icon>
          </span>
          <span class="item-icon error" v-else-if="item.zt === 'error'">
            <app-icon  class="app-icon" name="error"></app-icon>
          </span>

          <!-- 状态 -->
          <span class="item-status" v-if="item.zt === 'uploading'">{{ item.percentage }}</span>
          <span class="item-status success" v-else-if="item.zt === 'success'">已上传</span>
          <span class="item-status error" v-else-if="item.zt === 'error'">{{ item.error_info || '失败' }}</span>

          <!-- 视频文件 -->
          <template v-if="item.filetype && item.filetype.startsWith('video/')">
            <span class="item-name clickable" @click="previewVideo(item)" :title="item.filename">
              {{ item.filename }}
            </span>
            <button class="item-play-icon" @click="previewVideo(item)" title="播放">
              <app-icon  class="app-icon" name="play_circle"></app-icon>
            </button>
          </template>
          <!-- 链接 -->
          <template v-else>
            <span class="item-name clickable" @click="startEdit(index, item)" :title="item.url">
              {{ item.filename || item.url }}
            </span>
            <button class="item-edit-icon" @click="startEdit(index, item)" title="编辑">
              <app-icon  class="app-icon" name="edit"></app-icon>
            </button>
          </template>

          <button class="item-del" @click="delUrl(index)">
            <app-icon  class="app-icon" name="close"></app-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- 视频预览 -->
    <el-dialog :visible.sync="previewVisible" title="视频预览" append-to-body width="640px" :close-on-click-modal="true"
      @closed="onPreviewClosed">
      <div class="preview-box" v-if="previewUrl">
        <video :src="previewUrl" controls autoplay class="preview-video"></video>
      </div>
    </el-dialog>

    <!-- 链接编辑 -->
    <el-dialog :visible.sync="editVisible" title="编辑链接" append-to-body width="520px" :close-on-click-modal="true"
      @opened="$refs.editDialogTextarea.focus()">
      <textarea v-model="editText" placeholder="请输入链接地址" ref="editDialogTextarea" class="edit-textarea"
        rows="5"></textarea>
      <span slot="footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEditDialog">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import CryptoJS from 'crypto-js'
import COS from 'cos-js-sdk-v5'

const STORAGE_KEY = 'qhy_upload_v2_state'
const STORAGE_VERSION = 1

export default {
  name: 'qhyuploadv2',
  props: {
    limit: { type: Number, default: 200 }
  },
  data() {
    return {
      uploadType: 1,
      inputUrl: '',
      urlList: [],
      uploadQueue: [],
      isUploading: false,
      uploadInfo: null,
      clearAfterSubmit: true,
      previewVisible: false,
      previewUrl: '',
      editVisible: false,
      editIndex: null,
      editText: '',
    }
  },
  computed: {
    // 兼容旧制作页面读取上传列表
    url_list() {
      return this.urlList
    }
  },
  watch: {
    urlList: {
      deep: true,
      handler() { this.saveToStorage() }
    }
  },
  methods: {
    // 提交成功后清空链接（默认开启；关闭则保留列表）
    clear_url() {
      if (!this.clearAfterSubmit) return
      this.urlList = []
    },
    saveToStorage() {
      try {
        const data = this.urlList
          .filter(item => item.zt === 'uploading')
          .slice(0, 20)
          .map(item => ({
            uid: item.uid,
            zt: item.zt,
            filename: item.filename,
            filetype: item.filetype,
            fileHash: item.fileHash || '',
            fileSize: item.fileSize || 0,
            fileLastModified: item.fileLastModified || 0,
          }))
        if (data.length > 0) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, data }))
        } else {
          localStorage.removeItem(STORAGE_KEY)
        }
      } catch (e) { /* ignore */ }
    },
    restoreFromStorage() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved || saved.version !== STORAGE_VERSION || !Array.isArray(saved.data)) return
        const now = Date.now()
        const DAY = 24 * 60 * 60 * 1000
        this.urlList = saved.data
          .filter(item => !item.time || (now - item.time < DAY))
          .map(item => {
            if (item.zt === 'uploading') {
              return { ...item, zt: 'error', error_info: '上次上传中断，请重新上传', url: '' }
            }
            return item
          })
      } catch (e) { /* ignore */ }
    },

    get_urls(isList = 0) {
      if (isList === 0) {
        let urls = ''
        for (let i = 0; i < this.urlList.length; i++) {
          if (this.urlList[i].zt === 'uploading') {
            return { status: 100, msg: '请等待视频上传完成，或取消上传' }
          }
          if (this.urlList[i].zt === 'error') continue
          urls += this.urlList[i].url + '\n'
        }
        if (urls.length < 1) return { status: 100, msg: '可制作视频为空，请先上传视频' }
        return urls
      }
      const urls = []
      for (let i = 0; i < this.urlList.length; i++) {
        if (this.urlList[i].zt === 'uploading') {
          return { status: 100, msg: '请等待视频上传完成，或取消上传' }
        }
        if (this.urlList[i].zt === 'error') continue
        urls.push(this.urlList[i])
      }
      if (urls.length < 1) return { status: 100, msg: '可制作视频为空，请先上传视频' }
      return urls
    },

    async pasteClipboard() {
      try {
        const text = await navigator.clipboard.readText()
        this.inputUrl = text
      } catch (e) {
        this.$msg.error('无法读取剪贴板')
      }
    },
    confirmUrls() {
      const lines = this.inputUrl.trim().split('\n').filter(l => l.trim())
      for (const line of lines) {
        if (this.urlList.length >= this.limit) {
          this.$msg.warning('最多' + this.limit + '个链接')
          return
        }
        this.urlList.push({ url: line.trim(), zt: 'success', filename: line.trim() })
      }
      this.$msg.success('识别有效链接 ' + lines.length + ' 个')
      this.inputUrl = ''
    },

    handleFileDrop(e) {
      const files = Array.from(e.dataTransfer.files)
      this.processFiles(files)
    },
    handleFileSelect(e) {
      const files = Array.from(e.target.files)
      this.processFiles(files)
      e.target.value = ''
    },
    processFiles(files) {
      for (const file of files) {
        if (this.urlList.length >= this.limit) {
          this.$msg.warning('最多' + this.limit + '个文件')
          return
        }
        const valid = ['video/mp4', 'video/quicktime']
        if (!valid.includes(file.type)) {
          this.$msg.error(file.name + ' 仅支持 MP4/MOV 格式')
          continue
        }
        if (file.size > 600 * 1024 * 1024) {
          this.$msg.error(file.name + ' 超过 600MB 限制')
          continue
        }
        file.uid = Date.now() + '_' + Math.random().toString(36).slice(2, 8)
        this.generateHash(file).then(hash => {
          file.fileHash = hash
          this.urlList.push({
            uid: file.uid, url: '', percentage: '准备上传',
            zt: 'uploading', filename: file.name, filetype: file.type,
            _file: file,
            fileHash: hash,
            fileSize: file.size,
            fileLastModified: file.lastModified,
          })
          this.uploadQueue.push(file)
          this.processQueue()
        })
      }
    },

    generateHash(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          // 用 CryptoJS 对 ArrayBuffer 正确哈希
          const wordArray = CryptoJS.lib.WordArray.create(e.target.result)
          const contentHash = CryptoJS.SHA256(wordArray).toString()
          const meta = file.name + '|' + file.size + '|' + file.lastModified
          const raw = CryptoJS.SHA256(contentHash + meta).toString()
          resolve(raw.slice(0, 16))
        }
        reader.readAsArrayBuffer(file.slice(0, 1024 * 1024))
      })
    },

    processQueue() {
      if (this.isUploading || this.uploadQueue.length === 0) return
      this.isUploading = true
      const file = this.uploadQueue.shift()
      this.cosUploadFile(file)
    },

    async cosUploadFile(file, retry = 0) {
      const that = this
      const MAX_RETRY = 2
      try {
        const res = await this.$ajax.post('api/v1/users/upload_dz_cos', {
          type: file.type,
          filename: file.name,
          hash: file.fileHash,
        }, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
        if (!res.data || res.data.status !== 200) {
          this.$msg.error(res.data.msg || '获取上传凭证失败')
          this.updateFile(file, 'error', res.data.msg || '获取上传凭证失败')
          return
        }
        const cfg = res.data.data

        // 检查文件是否已被用户删除
        if (that.urlList.findIndex(f => f.uid === file.uid) === -1) {
          that.isUploading = false
          that.processQueue()
          return
        }

        const cos = new COS({
          SecretId: cfg.Credentials.TmpSecretId,
          SecretKey: cfg.Credentials.TmpSecretKey,
          SecurityToken: cfg.Credentials.Token,
          StartTime: cfg.startTime,
          ExpiredTime: cfg.ExpiredTime,
        })

        // 秒传检测
        let taskId
        try {
          await cos.headObject({
            Bucket: cfg.bucket,
            Region: cfg.region,
            Key: cfg.dz,
          })
          that.updateFile(file, 'success', cfg.down_dz)
          console.log('云端有文件', cfg.dz)
          return
        } catch (e) {
          console.log('云端无文件，开始上传', e)
        }

        // 分片上传
        await new Promise((resolve, reject) => {
          cos.uploadFile({
            Bucket: cfg.bucket,
            Region: cfg.region,
            Key: cfg.dz,
            Body: file,
            SliceSize: 1024 * 1024 * 5,
            onTaskReady(id) { taskId = id },
            onProgress(data) {
              const pct = Math.round(data.percent * 100)
              const loaded = Math.round(data.loaded / 1024 / 1024)
              const total = Math.round(data.total / 1024 / 1024)
              const idx = that.urlList.findIndex(f => f.uid === file.uid)
              if (idx !== -1) {
                that.urlList[idx].percentage = '上传中 ' + pct + '% (' + loaded + 'MB/' + total + 'MB)'
              } else {
                cos.cancelTask(taskId)
                reject(new Error('用户取消'))
              }
            }
          }, (err, data) => {
            if (err) { reject(err) } else { resolve() }
          })
        })

        that.updateFile(file, 'success', cfg.down_dz)
      } catch (e) {
        if (e.message === '用户取消') {
          that.isUploading = false
          that.processQueue()
          return
        }
        if (retry < MAX_RETRY) {
          const idx = that.urlList.findIndex(f => f.uid === file.uid)
          if (idx !== -1) {
            that.urlList[idx].percentage = '重试中 ' + (retry + 1) + '/' + MAX_RETRY
          }
          setTimeout(() => that.cosUploadFile(file, retry + 1), 1000)
        } else {
          that.updateFile(file, 'error', String(e))
        }
      }
    },

    updateFile(file, type, info) {
      const idx = this.urlList.findIndex(f => f.uid === file.uid)
      if (idx === -1) return
      if (type === 'success') {
        this.urlList[idx].url = info
        this.urlList[idx].zt = 'success'
        this.$msg.success(file.name + ' 上传成功')
      } else {
        this.urlList[idx].zt = 'error'
        this.urlList[idx].error_info = info
      }
      this.isUploading = false
      this.processQueue()
    },

    delUrl(index) {
      const item = this.urlList[index]
      this.$confirm('确定删除 ' + (item.filename || item.url) + '?', '提示', { type: 'warning' })
        .then(() => {
          if (item.zt === 'uploading') {
            const qi = this.uploadQueue.findIndex(f => f.uid === item.uid)
            if (qi !== -1) this.uploadQueue.splice(qi, 1)
          }
          this.urlList.splice(index, 1)
          if (this.editIndex === index) this.editIndex = null
        }).catch(() => { })
    },

    previewVideo(item) {
      if (item._file) {
        if (this._blobUrl) URL.revokeObjectURL(this._blobUrl)
        this._blobUrl = URL.createObjectURL(item._file)
        this.previewUrl = this._blobUrl
      } else if (item.url) {
        this.previewUrl = item.url
      } else {
        return
      }
      this.previewVisible = true
    },
    onPreviewClosed() {
      if (this._blobUrl) {
        URL.revokeObjectURL(this._blobUrl)
        this._blobUrl = null
      }
      this.previewUrl = ''
    },

    startEdit(index, item) {
      this.editIndex = index
      this.editText = item.url || ''
      this.editVisible = true
    },
    saveEditDialog() {
      if (this.editText && this.editText.trim()) {
        this.urlList[this.editIndex].url = this.editText.trim()
        this.urlList[this.editIndex].filename = this.editText.trim()
      }
      this.editVisible = false
      this.editText = ''
      this.editIndex = null
    },
  },
  mounted() {
    this.restoreFromStorage()
  }
}
</script>

<style scoped>
.upload-v2 {
  font-family: 'Inter', -apple-system, sans-serif;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
}

.upload-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f5f7fa;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 16px;
}

.tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-item.active {
  background: #1a73e8;
  color: #fff;
}

.tab-item .app-icon {
  font-size: 18px;
}

.tab-option {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 6px 10px;
  font-size: 11px;
  color: #94a3b8;
  background: #f1f5f9;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.tab-option.on {
  color: #16a34a;
  background: #dcfce7;
}

.upload-panel {
  margin-bottom: 16px;
}

.url-textarea {
  width: 100%;
  height: 125px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  font-size: 13px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}

.url-textarea:focus {
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
}

.panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.panel-actions .clear-option {
  margin-right: auto;
}

.btn-sm {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-sm .app-icon {
  font-size: 16px;
}

.btn-outline {
  background: #f1f5f9;
  color: #64748b;
}

.btn-outline:hover {
  background: #e2e8f0;
}

.btn-primary {
  background: #1a73e8;
  color: #fff;
}

.btn-primary:hover {
  background: #1557b0;
}

.upload-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafbfc;
}

.upload-drop:hover {
  border-color: #1a73e8;
  background: #f8faff;
}

.drop-icon {
  font-size: 40px;
  color: #1a73e8;
  margin-bottom: 8px;
}

.drop-text {
  font-size: 14px;
  color: #1a1a2e;
  margin: 0;
}

.drop-hint {
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0 0;
}

.upload-list {
  background: #fafbfc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  min-width: 0;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.list-title {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.list-body {
  max-height: 200px;
  overflow-y: auto;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid #f1f5f9;
  min-width: 0;
}

.list-item:last-child {
  border-bottom: none;
}

.item-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #1a73e8;
  color: #fff;
  border-radius: 6px;
  font-size: 11px;
  flex-shrink: 0;
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-icon .app-icon {
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-icon.success {
  color: #16a34a;
}

.item-icon.error {
  color: #ef4444;
}

.item-name {
  flex: 1;
  font-size: 13px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-name.clickable {
  cursor: pointer;
}

.item-name.clickable:hover {
  color: #1a73e8;
}

.item-edit-icon,
.item-play-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
  padding: 0;
  overflow: hidden;
}

.item-edit-icon .app-icon,
.item-play-icon .app-icon {
  font-size: 18px !important;
  transform: translateY(1px);
}

.item-edit-icon:hover {
  color: #1a73e8;
  background: #e3f2fd;
}

.item-play-icon:hover {
  color: #16a34a;
  background: #dcfce7;
}

.item-status {
  font-size: 11px;
  color: #94a3b8;
  flex-shrink: 0;
}

.item-status.success {
  color: #16a34a;
}

.item-status.error {
  color: #ef4444;
}

.item-del {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
}

.item-del .app-icon {
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-del:hover {
  background: #fee2e2;
  color: #ef4444;
}

.preview-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

.preview-video {
  width: 100%;
  max-height: 480px;
}

.edit-textarea {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}

.edit-textarea:focus {
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
}
</style>



// WEBPACK FOOTER //
// src/components/common/qhyuploadv2.vue